import { Input, InputGroup, InputLeftElement, Text } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { inventoryFieldProps } from '../../utils/inventoryFieldStyles';

interface CurrencyInputProps {
    value: number;
    onChange: (value: number) => void;
    isDisabled?: boolean;
    isReadOnly?: boolean;
    useThousandsSeparator?: boolean;
}

const formatCurrency = (n: number, useThousandsSeparator: boolean) =>
    useThousandsSeparator
        ? n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        : n.toFixed(2);

const CurrencyInput = ({
    value,
    onChange,
    isDisabled = false,
    isReadOnly = false,
    useThousandsSeparator = false,
}: CurrencyInputProps) => {
    const [text, setText] = useState('');
    const [focused, setFocused] = useState(false);

    useEffect(() => {
        if (!focused) {
            setText(value === 0 ? '' : formatCurrency(value, useThousandsSeparator));
        }
    }, [value, focused, useThousandsSeparator]);

    const commit = (raw: string) => {
        const trimmed = raw.trim();
        if (trimmed === '') {
            onChange(0);
            return;
        }
        const normalized = useThousandsSeparator ? trimmed.replace(/,/g, '') : trimmed;
        const n = parseFloat(normalized);
        if (!Number.isNaN(n) && n >= 0) {
            onChange(Math.round(n * 100) / 100);
        }
    };

    const displayValue = isReadOnly
        ? formatCurrency(value, useThousandsSeparator)
        : focused
          ? text
          : value === 0
            ? ''
            : formatCurrency(value, useThousandsSeparator);

    return (
        <InputGroup size="sm">
            <InputLeftElement pointerEvents="none" h="32px">
                <Text color="gray.500" fontSize="sm">
                    $
                </Text>
            </InputLeftElement>
            <Input
                pl={7}
                placeholder="0"
                value={displayValue}
                onChange={(e) => setText(e.target.value)}
                onFocus={() => {
                    if (isReadOnly) return;
                    setFocused(true);
                    setText(value === 0 ? '' : formatCurrency(value, useThousandsSeparator));
                }}
                onBlur={() => {
                    if (isReadOnly) return;
                    setFocused(false);
                    commit(text);
                }}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                        (e.target as HTMLInputElement).blur();
                    }
                }}
                isDisabled={isDisabled}
                readOnly={isReadOnly}
                bg={isReadOnly ? 'gray.50' : undefined}
                {...inventoryFieldProps}
            />
        </InputGroup>
    );
};

export default CurrencyInput;
